import React, { useState, useEffect, useRef } from "react";
import {
  Bell,
  MessageCircle,
  ChevronDown,
  User,
  Menu,
  Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

interface HeaderProps {
  onToggleSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    // Use AuthContext logout function
    logout();

    // Clear any additional stored data
    localStorage.removeItem("sidebarOpenSubmenu");
    localStorage.removeItem("sidebarOpenChildSubmenu");

    // Close dropdown
    setIsUserMenuOpen(false);

    // Navigate to login page
    navigate("/login");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left Side - Menu Button, Logo and Institution Name */}
        <div className="flex items-center space-x-3">
          {/* Hamburger Menu Button */}
          <button
            onClick={onToggleSidebar}
            className="text-[rgb(51,122,183)] hover:bg-gray-100 rounded transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="bg-[#0087C1] text-white font-bold text-xl px-3 py-1 rounded">
            VAWL
          </div>
          <div className="text-black font-medium">
            VIỆN KHOA HỌC THỦY LỢI VIỆT NAM
          </div>
        </div>

        {/* Right Side - Notifications and User */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative cursor-pointer">
            <Bell className="h-6 w-6 text-gray-400 hover:text-gray-600 transition-colors" />
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              13
            </span>
          </div>

          {/* Messages */}
          <div className="relative cursor-pointer">
            <MessageCircle className="h-6 w-6 text-gray-400 hover:text-gray-600 transition-colors" />
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              10
            </span>
          </div>

          {/* User Avatar with Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <div
              className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            >
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-gray-600" />
              </div>
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                  isUserMenuOpen ? "rotate-180" : ""
                }`}
              />
            </div>

            {/* Dropdown Menu */}
            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="py-1">
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center space-x-2"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate("/profile");
                    }}
                  >
                    <User className="h-4 w-4" />
                    <span>Thông tin tài khoản</span>
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center space-x-2"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate("/change-password");
                    }}
                  >
                    <Lock className="h-4 w-4" />
                    <span>Đổi mật khẩu</span>
                  </button>
                  <hr className="border-gray-200" />
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center space-x-2"
                    onClick={handleLogout}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    <span>Đăng xuất</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
